import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Users, EnvelopeOpen, Fire, Moon, UserCirclePlus, Lightning, CalendarCheck, EnvelopeSimple, Trophy } from "@phosphor-icons/react";

const STAGE_COLORS = {
  new: "#5AC8FA",
  warming: "#FF9500",
  engaged: "var(--accent)",
  active: "#34C759",
  dormant: "#8E8E93",
};

export default function DashboardOverview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const run = async () => {
      try {
        const r = await api.get("/dashboard/overview");
        setData(r.data);
      } catch { setData(null); }
    };
    run();
  }, []);
  if (!data) return null;

  const stages = ["new", "warming", "engaged", "active", "dormant"];

  return (
    <section className="mt-6 mb-6" data-testid="dashboard-overview">
      <div className="overline mb-3 flex items-center gap-2"><Users weight="bold" /> // relationships</div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {stages.map((s) => (
          <div key={s} className="border p-4" style={{ borderColor: "var(--border)" }} data-testid={`stage-${s}`}>
            <div className="overline" style={{ color: STAGE_COLORS[s] }}>{s}</div>
            <div className="text-3xl font-bold mt-1">{data.stage_counts?.[s] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        <OverviewCard
          icon={<UserCirclePlus weight="bold" />}
          title="needs follow-up"
          emptyText="Everyone is in touch."
          items={data.contacts_needing_follow_up}
          testid="needs-follow-up"
          render={(c) => (
            <Link to={`/contacts/${c.id}`} key={c.id} className="block py-2 border-b hover:bg-[#15303A]" style={{ borderColor: "var(--border)" }}>
              <div className="font-semibold truncate">{c.name}</div>
              <div className="overline">last contact: {c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString() : "never"}</div>
            </Link>
          )}
        />
        <OverviewCard
          icon={<CalendarCheck weight="bold" />}
          title="upcoming meetings"
          emptyText="No meetings this week."
          items={data.upcoming_meetings}
          testid="upcoming-meetings"
          render={(m) => (
            <Link to={m.contact_id ? `/contacts/${m.contact_id}` : "#"} key={m.id} className="block py-2 border-b hover:bg-[#15303A]" style={{ borderColor: "var(--border)" }}>
              <div className="font-semibold truncate">{m.title || "meeting"}</div>
              <div className="overline">{m.start_at ? new Date(m.start_at).toLocaleString() : ""}</div>
            </Link>
          )}
        />
        <OverviewCard
          icon={<EnvelopeOpen weight="bold" />}
          title="recent replies"
          emptyText="Nothing new in your inbox."
          items={data.recent_replies}
          testid="recent-replies"
          render={(r, i) => (
            <Link to={r.contact_id ? `/contacts/${r.contact_id}` : "#"} key={i} className="block py-2 border-b hover:bg-[#15303A]" style={{ borderColor: "var(--border)" }}>
              <div className="font-semibold truncate">{(r.subject || "").slice(0, 60) || "reply"}</div>
              <div className="overline">{r.received_at ? new Date(r.received_at).toLocaleString() : ""}</div>
            </Link>
          )}
        />
      </div>

      <div className="border p-4 mt-6" style={{ borderColor: "var(--border)" }} data-testid="top-opportunities">
        <div className="overline mb-3 flex items-center gap-2"><Trophy weight="bold" style={{ color: "#FFD60A" }} /> // top opportunities</div>
        {(data.top_opportunities || []).length === 0 ? (
          <div className="text-sm" style={{ color: "var(--text-2)" }}>No high-priority contacts flagged yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {data.top_opportunities.map((c) => (
              <Link key={c.id} to={`/contacts/${c.id}`} className="border p-3 hover:bg-[#15303A]" style={{ borderColor: "var(--border)" }}>
                <div className="font-semibold truncate">{c.name}</div>
                <div className="overline mt-1 flex items-center gap-2">
                  <span style={{ color: c.priority === "urgent" ? "#FF3B30" : "#FF9500" }}>{c.priority}</span>
                  <span>{c.stage}</span>
                  {c.estimated_value ? <span style={{ color: "#34C759" }}>${c.estimated_value}</span> : null}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function OverviewCard({ icon, title, emptyText, items, render, testid }) {
  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="overline mb-2 flex items-center gap-2">{icon} // {title}</div>
      {(items || []).length === 0 ? (
        <div className="text-sm" style={{ color: "var(--text-2)" }}>{emptyText}</div>
      ) : (
        <div className="divide-y" style={{ borderColor: "var(--border)" }}>{items.map(render)}</div>
      )}
    </div>
  );
}
